/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import { forwardRef, Fragment, type JSX } from "react";

import {
  constantSemanticColors,
  radius,
  spacing,
} from "../../../../../../global/emotion";
import { stylesOpacity } from "../../../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../../../global/emotion/styles/stylesScale";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import { HallowImage } from "../../../../blocks/other/HallowImage/HallowImage";
import type { MaskProps } from "../../../_core/Mask/Mask.emotion";
import { Mask } from "../../../_core/Mask/Mask.emotion";
import { Tag } from "../../../_core/Tag/Tag.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../../icons/Icon/GeneralIcons";
import type { CampaignLargeMobileProps } from "../CampaignLargeMobile/CampaignLargeMobile.emotion";
import { CampaignLargeMobile } from "../CampaignLargeMobile/CampaignLargeMobile.emotion";

const hallowElement: HallowElement = "div";

const actionsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};
const campaignLargeMobileStyle: Interpolation = {
  display: "flex",
  [cssMediaQueryMinWidthTablet]: { display: "none" },
};
const determinedElementStyle: Interpolation<CSSColorStrict> = (color) => ({
  backgroundColor: color,
  borderColor: color,
  borderRadius: radius.l,
  borderStyle: "solid",
  borderWidth: 8,
  boxSizing: "border-box",
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
  height: 480,
});
const illustrationImgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderRadius: spacing.s,
  height: 104,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  userSelect: "none",
  width: 104,
};
const imgContainerStyle: Interpolation = {
  flex: 3,
  position: "relative",
};
const imgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderBottomRightRadius: radius.m,
  borderTopRightRadius: radius.m,
  height: spacing.full,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  userSelect: "none",
  width: spacing.full,
};
const infoContainerStyle: Interpolation = {
  boxSizing: "border-box",
  color: constantSemanticColors.onColorHighest,
  display: "flex",
  flex: 2,
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "center",
  minWidth: 384,
  paddingLeft: 80,
};
const infoTopContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
};
const infoTopTopContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};
const maskStyle: Interpolation = {
  left: -1,
  position: "absolute",
};
const tagJoinedStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  maxWidth: 24,
};
const tagsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const tagWidthFitStyle: Interpolation = {
  width: "fit-content !important",
};

export type CampaignLargeProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  actions?: KeyedElement | KeyedElement[];
  activeState?: boolean;
  campaignLargeMobileProps?: Partial<CampaignLargeMobileProps>;
  color: CSSColorStrict;
  description: string;
  focusState?: boolean;
  hoverState?: boolean;
  illustrationImgSrc?: string;
  imgSrc?: Images;
  joined?: boolean;
  tags: string[];
  title: string;
  extraInfo?: React.ReactNode;
};

export const CampaignLarge = forwardRef<any, CampaignLargeProps>(
  (
    {
      actions,
      activeState = false,
      asChild = false,
      campaignLargeMobileProps = {},
      children,
      color,
      description,
      focusState = true,
      hoverState = true,
      illustrationImgSrc,
      imgSrc,
      joined,
      tags,
      title,
      extraInfo,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="infoContainer" css={infoContainerStyle}>
          <div css={infoTopContainerStyle}>
            <div css={infoTopTopContainerStyle}>
              {illustrationImgSrc && (
                <img
                  alt={title}
                  src={illustrationImgSrc}
                  css={illustrationImgStyle}
                />
              )}
              <Text overflow="ellipsis" size="m" type="title">
                {title}
              </Text>
            </div>
            <Text lineClamp={2} size="l" type="headline">
              {description}
            </Text>
            <div css={tagsContainerStyle}>
              {tags.map((tag) => (
                <Tag key={tag} css={tagWidthFitStyle} variant="lighten">
                  {tag}
                </Tag>
              ))}
              {joined && (
                <Tag variant="lighten" css={tagJoinedStyle}>
                  <CheckmarkIcon.SimpleOn size={12} />
                </Tag>
              )}
            </div>
          </div>
          {actions && (
            <div key="actions" css={actionsContainerStyle}>
              {actions}
            </div>
          )}
          {extraInfo}
        </div>,
        <div key="imgContainer" css={imgContainerStyle}>
          <Mask
            color={color as MaskProps["color"]}
            direction="to right"
            orientation="vertical"
            css={maskStyle}
          />
          <HallowImage alt={title} src={imgSrc} size={"l"} css={imgStyle} />
        </div>,
      ],
      children: children as JSX.Element,
    });

    return (
      <Fragment>
        <CampaignLargeMobile
          activeState={activeState}
          asChild={asChild}
          children={children}
          color={color}
          description={description}
          focusState={focusState}
          hoverState={hoverState}
          illustrationImgSrc={illustrationImgSrc}
          imgSrc={imgSrc}
          joined={joined}
          ref={ref}
          tags={tags}
          title={title}
          extraInfo={extraInfo}
          css={campaignLargeMobileStyle}
          {...campaignLargeMobileProps}
        />
        <DeterminedElement
          ref={ref}
          css={[
            determinedElementStyle(color),
            activeState ? stylesOpacity.active : null,
            activeState ? stylesOpacity.base : null,
            stylesOutline.base,
            focusState ? stylesOutline.focus : null,
            hoverState ? stylesOutline.hover : null,
            activeState && hoverState ? stylesScale.base : null,
            activeState && hoverState ? stylesScale.s : null,
          ]}
          {...props}
        >
          {determinedChildren}
        </DeterminedElement>
      </Fragment>
    );
  },
);

CampaignLarge.displayName = "CampaignLarge";
